/** @jsxImportSource @emotion/react */
import React, { useEffect } from 'react'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import {
  getHeaderContentHeight,
  setChromeContentHeights
} from '@purecarslabs/signal-layouts/utils'
import Page from '@purecarslabs/signal-layouts/baseline/Page'
import { AnyReactComponentType } from '@signal/utils/types'

const PublicPage: React.FC<{
  routeProps: React.PropsWithChildren<RouteComponentProps>
  withPageParams: WithPublicPageParamsType
}> = ({
  routeProps: { children, match, ...rest },
  withPageParams: {
    PageComponent,
    PublicHeader,
    PublicFooter,
    appMinWidth,
    headerProps = {},
    hideChrome,
    hideFooter
  }
}) => {
  const rawTitle = headerProps.title
  const resolvedTitle =
    typeof rawTitle === 'function'
      ? rawTitle((match?.params ?? {}) as Record<string, unknown>)
      : rawTitle
  const resolvedHeaderProps = { ...headerProps, title: resolvedTitle }

  // No shared chrome to set the tab title, so do it here (also covers hideChrome routes).
  useEffect(() => {
    if (typeof resolvedTitle === 'string' && resolvedTitle) {
      document.title = resolvedTitle
    }
  }, [resolvedTitle])

  const header =
    hideChrome || !PublicHeader ? (
      undefined
    ) : (
      <PublicHeader {...resolvedHeaderProps} />
    )
  const footer =
    hideChrome || hideFooter || !PublicFooter ? (
      undefined
    ) : (
      <PublicFooter {...resolvedHeaderProps} />
    )

  return (
    <Page
      appMinWidth={appMinWidth}
      // Page never re-measures without a header, so a non-zero default there
      // would leave a permanent phantom gap.
      defaultHeaderHeight={header ? parseInt(getHeaderContentHeight(true)) : 0}
      header={header}
      footer={footer}
    >
      <PageComponent match={match} {...rest}>
        {children}
      </PageComponent>
    </Page>
  )
}

export type WithPublicPageParamsType = {
  PageComponent: AnyReactComponentType
  PublicHeader?: AnyReactComponentType
  PublicFooter?: AnyReactComponentType
  publicHeaderHeight?: string
  publicFooterHeight?: string
  appMinWidth?: number
  headerProps?: Record<string, unknown>
  hideChrome?: boolean
  hideFooter?: boolean
  withRouterFunc?: typeof withRouter
}

const withPublicPage = (params: WithPublicPageParamsType) => {
  const withRouterFunc = params.withRouterFunc || withRouter
  // Before the first Page render, so the layout helpers stop reporting hub sizes.
  setChromeContentHeights(params.publicHeaderHeight, params.publicFooterHeight)
  return withRouterFunc(
    (routeProps: React.PropsWithChildren<RouteComponentProps>) => (
      <PublicPage routeProps={routeProps} withPageParams={params} />
    )
  )
}

export default withPublicPage
